import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Loader2, Shield, CheckCircle, Clock, ExternalLink, Copy, Mail, Info, AlertTriangle, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return ' - ';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short',
    });
  } catch { return iso; }
};

const fmtCurrency = (n) => new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
}).format(n || 0);

const short = (hex, left = 10, right = 6) =>
  hex && hex.length > left + right ? `${hex.slice(0, left)}…${hex.slice(-right)}` : hex || ' - ';

const VERIFICATION_COPY = {
  verified: { icon: CheckCircle, color: 'text-emerald-400', text: '' },
  complete: { icon: CheckCircle, color: 'text-emerald-400', text: '' },
  pending: { icon: Clock, color: 'text-amber-400', text: 'Pending' },
  not_recorded: { icon: AlertTriangle, color: 'text-neutral-500', text: 'Not recorded' },
  not_commissioned: { icon: Info, color: 'text-neutral-500', text: 'Not commissioned' },
};

export default function Provenance() {
  const { assetId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    document.title = 'Provenance Record · Assagart';
    // noindex meta tag - direct link only
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/provenance/${assetId}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Unable to load record.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assetId]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1800);
    } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={32} className="text-gold animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <AlertTriangle size={36} className="text-amber-400 mx-auto mb-4" />
          <h1 className="font-serif text-xl text-white mb-2">Record not available</h1>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const { reference_code, issued_at, asset, registry, ownership, valuation, valuation_disclosed,
          fractional, verification, audit_trail, contact_email, disclaimer } = data;

  const contractUrl = registry?.explorer_url && registry?.contract_address
    ? `${registry.explorer_url.replace(/\/$/, '')}/address/${registry.contract_address}` : null;
  const txUrl = registry?.explorer_url && registry?.tx_hash
    ? `${registry.explorer_url.replace(/\/$/, '')}/tx/${registry.tx_hash}` : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans" data-testid="provenance-page">
      <div className="max-w-3xl mx-auto px-6 py-16">

        {/* Header */}
        <header className="mb-12" data-testid="provenance-header">
          <img src="/assagart-logo.png" alt="Assagart" className="h-16 mb-5" />
          <h1 className="font-serif text-3xl text-gold tracking-widest">ASSAGART</h1>
          <p className="text-[11px] text-muted-foreground uppercase tracking-[0.3em] mt-1">
            Provenance Record
          </p>
          <div className="mt-6 pt-5 border-t border-[#1a1a1a] grid grid-cols-2 gap-4 text-[12px]">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Reference</p>
              <p className="font-mono text-white mt-1" data-testid="ref-code">{reference_code}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Issued</p>
              <p className="font-mono text-white mt-1">{fmtDate(issued_at)}</p>
            </div>
          </div>
        </header>

        {/* Asset ID card */}
        <section className="mb-10" data-testid="asset-section">
          <div className="flex gap-5">
            {asset.image_url && (
              <div className="w-32 h-32 flex-shrink-0 rounded bg-[#0A0A0A] border border-[#1a1a1a] overflow-hidden">
                <img src={asset.image_url} alt={asset.identifier} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Asset Class</p>
              <p className="text-white text-sm mt-0.5">{asset.asset_class}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-3">Category</p>
              <p className="text-white text-sm mt-0.5 capitalize">{asset.category || ' - '}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-3">Identifier</p>
              <p className="font-serif text-white text-lg mt-0.5">{asset.identifier}</p>
            </div>
          </div>
        </section>

        <SectionDivider label="Registry Details" />
        <section className="mb-10" data-testid="registry-section">
          <dl className="grid grid-cols-1 gap-2.5 text-[13px]">
            <Row label="Platform" value={registry.platform} />
            <Row label="Network" value={`${registry.network || ' - '} (Chain ID ${registry.chain_id || ' - '})`} />
            <Row label="Contract" value={
              contractUrl ? (
                <a href={contractUrl} target="_blank" rel="noopener noreferrer"
                   className="font-mono text-[#627EEA] hover:text-[#8aa4ee] inline-flex items-center gap-1 break-all"
                   data-testid="contract-link">
                  {registry.contract_address}<ExternalLink size={11} />
                </a>
              ) : <span className="font-mono">{short(registry.contract_address, 12, 10)}</span>
            } />
            <Row label="Token ID" value={<span className="font-mono">#{registry.token_id ?? ' - '}</span>} />
            <Row label="Transaction" value={
              txUrl ? (
                <a href={txUrl} target="_blank" rel="noopener noreferrer"
                   className="font-mono text-[#627EEA] hover:text-[#8aa4ee] inline-flex items-center gap-1 break-all"
                   data-testid="tx-link">
                  {short(registry.tx_hash, 14, 10)}<ExternalLink size={11} />
                </a>
              ) : <span className="font-mono text-muted-foreground">Pending</span>
            } />
            <Row label="Issuance Date" value={fmtDate(registry.issued_at)} />
            <Row label="Status" value={
              <span className="inline-flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${registry.tx_hash ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                {registry.status}
              </span>
            } />
          </dl>
        </section>

        <SectionDivider label="Ownership Structure" />
        <section className="mb-10" data-testid="ownership-section">
          <dl className="grid grid-cols-1 gap-2.5 text-[13px]">
            <Row label="Primary Holder" value={<span className="font-mono">{ownership.holder_ref}</span>} />
            <Row label="Jurisdiction" value={ownership.jurisdiction} />
            <Row label="Custody" value={ownership.custody} />
          </dl>
          <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed border-l-2 border-[#1a1a1a] pl-3">
            {ownership.identity_disclosure_note}
          </p>
        </section>

        <SectionDivider label="Valuation" />
        <section className="mb-10" data-testid="valuation-section">
          {valuation_disclosed && valuation ? (
            <dl className="grid grid-cols-1 gap-2.5 text-[13px]">
              <Row label="Current Valuation" value={<span className="text-gold text-base">{fmtCurrency(valuation.current_value_gbp)}</span>} />
              <Row label="Last Revaluation" value={fmtDate(valuation.last_revaluation_at)} />
              <Row label="Methodology" value={<span className="text-[12px] text-muted-foreground leading-relaxed">{valuation.methodology}</span>} />
            </dl>
          ) : (
            <div className="border border-dashed border-[#1a1a1a] rounded p-4 text-[12px] text-muted-foreground leading-relaxed">
              Valuation disclosure has not been enabled for this record. Advisors
              may request the current valuation by quoting reference <span className="font-mono text-white">{reference_code}</span>
              {' '}to <a href={`mailto:${contact_email}`} className="text-[#627EEA] hover:text-[#8aa4ee]">{contact_email}</a>.
            </div>
          )}
        </section>

        <SectionDivider label="Fractional Ownership" />
        <section className="mb-10" data-testid="fractional-section">
          <dl className="grid grid-cols-1 gap-2.5 text-[13px]">
            <Row label="Total Fractions" value={<span className="font-mono">{(fractional.total_fractions || 0).toLocaleString()}</span>} />
            <Row label="Outstanding" value={
              <span className="font-mono">
                {(fractional.outstanding || 0).toLocaleString()} ({((fractional.outstanding / Math.max(1, fractional.total_fractions)) * 100).toFixed(1)}%)
              </span>
            } />
            <Row label="Holder Count" value={<span className="font-mono">{fractional.holder_count}</span>} />
            <Row label="Distribution" value={
              <span>
                <span className={fractional.distribution_label === 'Concentrated' ? 'text-amber-400' : 'text-emerald-400'}>
                  {fractional.distribution_label}
                </span>
                <span className="text-muted-foreground ml-2"> - {fractional.distribution_summary}</span>
              </span>
            } />
          </dl>

          {fractional.holder_count > 0 && (
            <details open={advancedOpen} onToggle={(e) => setAdvancedOpen(e.target.open)} className="mt-5 border-t border-[#1a1a1a] pt-4">
              <summary className="cursor-pointer text-[11px] text-muted-foreground uppercase tracking-wider flex items-center gap-2 hover:text-white transition-colors">
                <ChevronDown size={12} className={`transform transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                Concentration Analysis
              </summary>
              <div className="mt-4 space-y-2.5 text-[12px]">
                <Row label="Largest holder" value={<span className="font-mono">{fractional.concentration.top_1_pct}%</span>} />
                <Row label="Top 5 holders" value={<span className="font-mono">{fractional.concentration.top_5_pct}%</span>} />
                <Row label="Top 20 holders" value={<span className="font-mono">{fractional.concentration.top_20_pct}%</span>} />
                <Row label="Gini coefficient" value={<span className="font-mono">{fractional.concentration.gini_coefficient}</span>} />
              </div>
              <p className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
                Gini closer to 0 = evenly distributed. Closer to 1 = highly concentrated.
              </p>
            </details>
          )}
        </section>

        <SectionDivider label="Verification" />
        <section className="mb-10" data-testid="verification-section">
          <ul className="space-y-2.5 text-[13px]">
            <VerifRow label="Physical inspection" item={verification.physical_inspection} />
            <VerifRow label="Serial / hallmark" item={verification.serial_or_hallmark} />
            <VerifRow label="Documentation archived" item={verification.documentation_archived} />
            <VerifRow label="Independent appraiser" item={verification.independent_appraiser} />
            <VerifRow label="Third-party authenticator" item={verification.third_party_authenticator} />
          </ul>
        </section>

        <SectionDivider label="Audit Trail" />
        <section className="mb-12" data-testid="audit-section">
          {audit_trail.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No events recorded.</p>
          ) : (
            <ol className="relative border-l border-[#1a1a1a] pl-5 space-y-5">
              {audit_trail.map((e, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[27px] top-1.5 w-2 h-2 rounded-full bg-gold" />
                  <p className="text-[10px] text-muted-foreground font-mono">{fmtDate(e.at)}</p>
                  <p className="text-white text-[13px] mt-0.5">{e.event}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {e.detail}
                    {e.explorer_url && (
                      <a href={e.explorer_url} target="_blank" rel="noopener noreferrer" className="text-[#627EEA] hover:text-[#8aa4ee] ml-1">[view on explorer ↗]</a>
                    )}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Share */}
        <SectionDivider label="Share" />
        <section className="mb-12 flex flex-wrap items-start gap-6" data-testid="share-section">
          <div className="p-3 bg-white rounded">
            <QRCodeSVG value={shareUrl} size={96} bgColor="#ffffff" fgColor="#050505" level="M" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Direct Link</p>
            <p className="text-[11px] font-mono text-white break-all bg-[#0A0A0A] border border-[#1a1a1a] rounded p-2">
              {shareUrl}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyLink} data-testid="copy-link-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1a1a1a] hover:border-gold/40 rounded text-[11px] text-white transition-colors">
                {copiedLink ? <CheckCircle size={12} className="text-emerald-400" /> : <Copy size={12} />}
                {copiedLink ? 'Copied' : 'Copy link'}
              </button>
              <a href={`mailto:?subject=Assagart Provenance Record - ${reference_code}&body=${encodeURIComponent(`Provenance record: ${shareUrl}`)}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1a1a1a] hover:border-gold/40 rounded text-[11px] text-white transition-colors"
                data-testid="email-link-btn">
                <Mail size={12} /> Email this record
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="pt-8 border-t border-[#1a1a1a] space-y-4">
          <p className="text-[10px] text-muted-foreground leading-relaxed max-w-prose">
            {disclaimer}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Questions? <a href={`mailto:${contact_email}`} className="text-gold hover:text-[#F4D03F]">{contact_email}</a>
          </p>
          <p className="text-center italic font-serif text-[11px] text-[#5a4a1a] tracking-[0.3em] pt-6">
            Per Illos Qui Perdurant
          </p>
        </footer>

      </div>
    </div>
  );
}

const SectionDivider = ({ label }) => (
  <div className="mb-5 pt-2">
    <p className="text-[10px] text-gold uppercase tracking-[0.3em]">{label}</p>
    <div className="h-px bg-[#1a1a1a] mt-2" />
  </div>
);

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[180px_1fr] gap-3 items-baseline">
    <dt className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</dt>
    <dd className="text-white">{value ?? ' - '}</dd>
  </div>
);

const VerifRow = ({ label, item }) => {
  const cfg = VERIFICATION_COPY[item?.status] || VERIFICATION_COPY.pending;
  const Icon = cfg.icon;
  return (
    <li className="grid grid-cols-[180px_1fr] gap-3 items-baseline">
      <span className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</span>
      <span className={`inline-flex items-center gap-2 ${cfg.color}`}>
        <Icon size={12} />
        <span className="text-white">{cfg.text || (item?.at ? fmtDate(item.at) : 'Verified')}</span>
      </span>
    </li>
  );
};
